import { io } from 'socket.io-client';

let socket = null;

// In demo mode, return a stub that quietly absorbs all listeners
// so no real network connection is attempted.
function makeStub() {
  const handlers = {};
  return {
    on: (event, fn) => { (handlers[event] = handlers[event] || []).push(fn); return socket; },
    off: (event, fn) => { if (handlers[event]) handlers[event] = handlers[event].filter((h) => h !== fn); return socket; },
    emit: () => socket,
    disconnect: () => {},
    connected: false,
  };
}

// Transport order + reconnection backoff — PR #330.
//
// Until PR #330, transports were ['websocket', 'polling'] with default
// reconnection (delay 1s, no max, no jitter). Behind the Render origin +
// Cloudflare HTTP/2 edge, the direct WSS upgrade currently returns HTTP 400
// (Socket.IO code 3 "Bad request"). The client retried forever with no
// backoff, saturating Chrome's 6-connection-per-host budget and starving
// concurrent XHR like POST /sms/send — which presented as a dead send
// button in the LeadDrawer.
//
// Fix: poll first, then OPPORTUNISTICALLY upgrade to websocket. This is the
// Socket.IO default ordering and is the documented recommendation behind
// HTTP/2 proxies. If the upgrade fails, polling stays up — we keep realtime
// status callbacks (message:new, message:status-update) flowing instead of
// losing them entirely. Reconnection backoff caps reconnect storms.
//
// Removal criteria: when the Render origin reliably accepts WSS upgrades,
// we can revisit transport order. Backoff stays regardless.
const SOCKET_OPTS_BASE = {
  transports: ['polling', 'websocket'],
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 30000,
  randomizationFactor: 0.5,
  // Give slow Render cold-starts / proxies time to answer the first handshake.
  timeout: 20000,
};

// Resolved on EVERY (re)connect attempt, so a token refreshed after login or
// rotation is what the server sees — not the one captured at first connect.
function dynamicAuth(cb) {
  let token = null;
  try { token = localStorage.getItem('civix_token'); } catch (_) { /* sandboxed */ }
  cb({ token });
}

export function getSocket() {
  if (socket) return socket;
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    socket = makeStub();
    return socket;
  }
  // Resolve socket origin from VITE_API_URL (e.g. "https://api.civixcrm.ai").
  // Strip trailing "/api" if present so socket.io connects to the server root.
  const rawApiUrl = import.meta.env.VITE_API_URL || '';
  let socketUrl = rawApiUrl ? rawApiUrl.replace(/\/api\/?$/, '') : undefined;
  socket = socketUrl
    ? io(socketUrl, {
        ...SOCKET_OPTS_BASE,
        auth: dynamicAuth,
        withCredentials: true,
      })
    : io({
        ...SOCKET_OPTS_BASE,
        auth: dynamicAuth,
      });
  return socket;
}

// Test-only seam: read the resolved option set so we can assert transport
// order and backoff parameters without poking at the live socket instance.
export function __getSocketOptsForTests() {
  return { ...SOCKET_OPTS_BASE };
}

export function disconnectSocket() {
  if (socket) {
    if (socket.disconnect) socket.disconnect();
    socket = null;
  }
}
